import PersonIcon from "@material-ui/icons/Person";
import { createSelector } from "@reduxjs/toolkit";
import { isNil } from "lodash";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ISelectOption } from "../../../../components/DataTable/DataTableFilters/DataTableFilterTypes/SelectFilter";
import { MenuAction, MenuActionItem } from "../../../../components/DataTable/DataTableHeading/DataTableHeading";
import { QParkChip } from "../../../../components/QParkChip";
import useFleetManagerContext from "../../../../context/hooks/useFleetManagerContext";
import useMyProductsContext from "../../../../context/hooks/useMyProductsContext";
import { convertIsoCountryCodeToNumberPlatePrefix } from "../../../../lib/countriesNumberPlates";
import { ParkingProductType } from "../../../../models";
import { FlexBudgetUnitType } from "../../../../models/delegations/DelegatedParkingProductsModel";
import { ContractType } from "../../../../models/ParkingProductModel";
import { RootState, useAppSelector } from "../../../../store/hooks";
import DoorIcon from "../../../../ui/Icons/DoorIcon";
import VehicleIcon from "../../../../ui/Icons/Vehicle";
import { NumberPlateDeSupport } from "../../../../ui/NumberPlate";

export type ProductChipProps = Readonly<{
  name: string;
  location: string;
}>;

export function ProductChip(props: ProductChipProps) {
  return <QParkChip.Labeled label={`${props.name} - ${props.location}`} />;
}

export type NumberPlateProps = Readonly<{
  countryCode: string;
  identifier: string;
}>;

export function NumberPlate(props: NumberPlateProps) {
  const numberPlatePrefix = convertIsoCountryCodeToNumberPlatePrefix(props.countryCode);
  return (
    <NumberPlateDeSupport prefix={numberPlatePrefix} value={props.identifier} readOnly compact />
  );
}

function makeFeatureFlagSelector(name: string, defaultValue = false) {
  return (state: RootState): boolean =>
    state.environment.featureFlags.find(f => f.name === name)?.value ?? defaultValue;
}

export function selectUseBulkVehicleAssignments(state: RootState) {
  return state.parkingProducts.hasUnlimitedEntryRights;
}

export const selectUseDirectVehicleAssignment = makeFeatureFlagSelector("UseDirectVehicleAssignment", true);
export const selectUseEntryRightsForFleetMembers = makeFeatureFlagSelector("UseEntryRightsForFleetMembers");
// when you remove this you should be able to remove ParkingRightForm too
export const selectEnableDelegationsScheduling = makeFeatureFlagSelector("EnableDelegationsScheduling");
export const selectEnableFleetVehicleScheduling = makeFeatureFlagSelector("EnableFleetVehicleScheduling");
export const selectBusinessSchedulingAdditions = makeFeatureFlagSelector("BusinessSchedulingAdditions");
export const selectShowActiveSession = makeFeatureFlagSelector("ShowActiveSession");

export function selectDefaultRevocationEmailValue(state: RootState) {
  return state.environment.dynamicConfigurations.find(c => c.name === "DefaultRevocationEmailValue")?.value === "True";
}

export function useGetOccupiedParkingRightsCount() {
  const useDirectVehicleAssignment = useAppSelector(selectUseDirectVehicleAssignment);

  return useCallback((pr: {totalAssignedParkingRights?: number, totalDelegatedParkingRights?: number}) => {
    if (useDirectVehicleAssignment) {
      return (pr.totalAssignedParkingRights ?? 0) + (pr.totalDelegatedParkingRights ?? 0);
    }

    return pr.totalDelegatedParkingRights ?? 0;
  }, [useDirectVehicleAssignment])
}

export const selectHasPedestrianProducts = createSelector(
    [
        (state: RootState) => state.parkingProducts.locatedParkingProducts.data,
        (state: RootState) => state.parkingProducts.parkingProducts.data,
    ],
    (locatedProducts, parkingProducts) => {
        return (
            locatedProducts?.some((lp) =>
                lp.parkingProducts.some((lpp) => lpp.productType === ParkingProductType.PedestrianAccess)
            ) || parkingProducts?.some((p) => p.productType === ParkingProductType.PedestrianAccess)
        );
    }
);

export function useSharedProductsMenuAction(isGoBusinessProductSelected = false): {
    menu: MenuAction;
    onAddDelegee: () => void;
    onAssignVehicle: () => void;
} {
    const { t } = useTranslation();
    const showDva = useAppSelector(selectDoShowDva);
    const hasPaProducts = useAppSelector(selectHasPedestrianProducts);
    const pedestrianRightsEnabled = hasPaProducts;

    const {
        toggleAssignVehicle,
        toggleAddDelegee,
        setSelectedParkingRightIds,
        toggleMultipleVehicles,
        toggleEntryRightInvitationForm,
    } = useFleetManagerContext();

    const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);

    return useMemo(() => {
        const onInviteFleetMemberClicked = () => {
            setSelectedParkingRightIds([]);
            toggleAddDelegee();
        };
        const onAssignVehicleClicked = () => {
            setSelectedParkingRightIds([]);
            toggleAssignVehicle();
        };
        const onWhitelistVehiclesClicked = () => {
            setSelectedParkingRightIds([]);
            toggleMultipleVehicles();
        };
        const onPedestrianRightInvitationFormClicked = () => {
            setSelectedParkingRightIds([]);
            toggleEntryRightInvitationForm();
        };

        const menuItems: MenuActionItem[] = [];

        if (!useBulkVehicleAssignments) {
            menuItems.push({
                label: t("fleetManagerTable:parkingRightInvitation"),
                action: onInviteFleetMemberClicked,
                icon: <PersonIcon />,
            });
        }

        if (pedestrianRightsEnabled && !isGoBusinessProductSelected) {
            menuItems.push({
                label: t("fleetManagerTable:pedestrianRightInvitation"),
                action: onPedestrianRightInvitationFormClicked,
                icon: <DoorIcon />,
            });
        }

        if (showDva && !isGoBusinessProductSelected) {
            menuItems.push({
                label: t("fleetManagerTable:assignVehicle"),
                action: onAssignVehicleClicked,
                icon: <VehicleIcon />,
            });
        }

        if (useBulkVehicleAssignments && !isGoBusinessProductSelected) {
            menuItems.push({
                label: t("fleetManagerTable:whitelistVehicles"),
                action: onWhitelistVehiclesClicked,
                icon: <VehicleIcon />,
            });
        }

        return {
            menu: {
                useMenu: true,
                showMenu: true,
                menuItems,
            },
            onAddDelegee: onInviteFleetMemberClicked,
            onAssignVehicle: onAssignVehicleClicked,
        };
    }, [
        t,
        toggleAssignVehicle,
        toggleAddDelegee,
        setSelectedParkingRightIds,
        toggleMultipleVehicles,
        pedestrianRightsEnabled,
        isGoBusinessProductSelected,
        showDva,
    ]);
}

export type UseProductOptionsParams = { productType?: ParkingProductType; contractType?: ContractType };

export function useProductOptions({ productType, contractType }: UseProductOptionsParams = {}): ISelectOption[] {
  const currentLocation = useCurrentlySelectedParkingProduct();

  return useMemo(() => {
    if (isNil(currentLocation)) return [];
    return currentLocation.parkingProducts
        .filter(x => (!productType || x.productType == productType) &&
            (!contractType || x.contractType === contractType))
        .map((product) => ({
            label: `${product.pmcName} - ${product.location}`,
            value: product.pmcId ?? 0,
        }));
  }, [currentLocation, productType, contractType]);
}

export function useCurrentlySelectedParkingProduct() {
  const {
    myProductsState: { selectedLocation },
  } = useMyProductsContext();
  const locatedProduct = useAppSelector((state) =>
    state.parkingProducts.locatedParkingProducts.data?.find?.(
      (loc) => loc.placeId === selectedLocation.placeId && loc.contractType == selectedLocation.contractType
    )
  );
  return locatedProduct;
}

export function convertToRemainingFlex(flexBudRemainingTime: string | undefined, flexBudUnitType: FlexBudgetUnitType | undefined): string {
  if (!flexBudRemainingTime || flexBudRemainingTime == '') {
    return 'N/A';
  }

  const match = flexBudRemainingTime.match(/(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/);

  if(!match) {
    return '';
  }

  const [_, dayPart, hoursPart, minutesPart] = match;
  const days: number = dayPart ? Number(dayPart) : 0;
  const hours: number = Number(hoursPart);
  const minutes: number = Number(minutesPart);

  const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
  const totalHours = Math.floor(totalMinutes / 60);

  if (flexBudUnitType === FlexBudgetUnitType.Hours) {
    return `${totalHours}h`;
  }

  if (flexBudUnitType === FlexBudgetUnitType.Minutes) {
    if (totalHours !== 0) {
      return `${totalHours}h ${minutes}min`;
    }

    return `${minutes}min`;
  }

  if (flexBudUnitType === FlexBudgetUnitType.Quarters) {
    const roundedMinutes = Math.floor(minutes / 15) * 15;
    if (totalHours != 0) {
      return `${totalHours}h ${roundedMinutes}min`;
    }

    return `${roundedMinutes}min`;
  }

  return '';
}
export enum TabOption {
  FleetMembers = "fleetMembers",
  PedestrianRights = "pedestrianRights",
  Vehicles = "vehicles"
}

export type EntryRightProductKey = { delegeeId: string, entryRightId?: string; pmcId?: number };

export function isEntryRightsMyProductsRowKey(obj: any): obj is EntryRightProductKey {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof obj.delegeeId === "string" &&
    (typeof obj.entryRightId === "string" || typeof obj.entryRightId === "undefined") &&
    (typeof obj.pmcId === "number" || typeof obj.pmcId === "undefined")
  );
}

export function isEntryRightsMyProductsRowKeyArray(obj: any): obj is EntryRightProductKey[] {
  return Array.isArray(obj) && obj.every(isEntryRightsMyProductsRowKey);
}

export function isStringArray(obj: any): obj is string[] {
  return Array.isArray(obj) && obj.every((item) => typeof item === "string");
}

export const selectDoShowDva = createSelector(
    [
        selectUseDirectVehicleAssignment,
        (state: RootState) => {
            const uniqueContractTypes = new Set<ContractType>();

            for (const lp of state.parkingProducts.locatedParkingProducts.data ?? []) {
                for (const p of lp.parkingProducts) {
                    uniqueContractTypes.add(p.contractType);
                }
            }

            for (const p of state.parkingProducts.parkingProducts.data ?? []) {
                uniqueContractTypes.add(p.contractType);
            }

            return uniqueContractTypes.size === 1 && uniqueContractTypes.has(ContractType.GoBusiness);
        },
    ],
    (dvaFf, onlyHasGoBusiness) => {
        if (!dvaFf) {
            return false;
        }

        return !onlyHasGoBusiness;
    }
);
